// libs
import { useMutation, useQuery } from '@apollo/client';
import clsx from 'clsx';
import { format } from 'date-fns';
import phoneValidator from 'phone';
import { ChangeEvent, FC, FormEvent, useEffect, useState } from 'react';

// graphql
import { LIST_CLINICIANS_QUERY } from '../../../graphql/query/clinicians';

// types
import {
  Clinic,
  ClinicianView,
  GraphQLResponse,
  GraphQLResponseTitan,
  IPatient,
  ISelectOption,
  PATIENT_PROGRESS,
  RISK_LEVEL,
  RiskLevelMap,
  SelectOptionClinician,
} from '../../../types';

// hooks
import useAuth from '../../../hooks/auth';
import useToaster from '../../../hooks/toast';

// components
import Autocomplete from '../../../components/form/autocomplete';
import Button from '../../../components/form/button';
import Checkbox from '../../../components/form/checkbox';
import DatePicker from '../../../components/form/date';
import Input from '../../../components/form/input';
import Select from '../../../components/form/select';
import Loader from '../../../components/loader';
import Modal from '../../../components/modal/default';
import ClinicianOption from '../../../components/select-options/clinician';

// styles
import {
  CreatePatientInput,
  IInvitePatientArgs,
  PATIENT_CREATE,
  PATIENT_INVITE,
} from '../../../graphql/mutation/patients';
import { CLINICS_GET } from '../../../graphql/query/clinics';
import { logger } from '../../../utils/logger';
import { CountryCode, normalizePhoneNumber } from '../../../utils/phone';
import styles from './styles.module.css';

type InputFieldKey = 'nameFirst' | 'nameLast' | 'email' | 'phone';
type DateFieldKey = 'dob' | 'sessionDate';
type CheckboxFieldKey = 'allowEmail' | 'allowSMS';
type SelectFieldKey = 'riskLevel' | 'clinicId' | 'progress';

interface IForm {
  nameFirst: string;
  nameLast: string;
  dob: Date | null;
  email: string;
  phone: string;
  riskLevel: RISK_LEVEL;
  allowEmail: boolean;
  allowSMS: boolean;
  clinicId: string;
  clinicianId: string | null;
  sessionDate: Date | null;
  progress: PATIENT_PROGRESS;
}

type FormErrors = { [key in keyof IForm]?: string } & { sendInvite?: string };

const defaults: IForm = {
  nameFirst: '',
  nameLast: '',
  dob: null,
  email: '',
  phone: '',
  riskLevel: RISK_LEVEL.LOW,
  allowEmail: false,
  allowSMS: false,
  clinicId: '',
  clinicianId: '',
  sessionDate: null,
  progress: PATIENT_PROGRESS.REFERRAL,
};

const errorDefaults: FormErrors = {
  nameFirst: '',
  nameLast: '',
  dob: '',
  email: '',
  phone: '',
  riskLevel: '',
  allowEmail: '',
  allowSMS: '',
  clinicId: '',
  clinicianId: '',
  sessionDate: '',
  progress: '',
  sendInvite: '',
};

const onFilterClinician =
  (filter: string) =>
  (option: ISelectOption<ClinicianView>): boolean => {
    const lc = filter.toLowerCase();
    const { name, email } = option.data as ClinicianView;
    return name.toLowerCase().includes(lc) || email.toLowerCase().includes(lc);
  };

const riskLevels: ISelectOption[] = Object.keys(RiskLevelMap).map(l => ({
  value: l,
  label: l,
}));

const PatientsRegisterView: FC = () => {
  const [form, setForm] = useState<IForm>(defaults);
  const [errors, setErrors] = useState<FormErrors>({ ...errorDefaults });
  const [isOpen, setIsOpen] = useState(false);
  const [signupCode, setSignupCode] = useState('');
  const [error, setError] = useState('');
  const [clinicians, setClinicians] = useState<SelectOptionClinician[]>([]);
  const [sendInvite, setSendInvite] = useState(true);
  const { user: currentUser } = useAuth();
  const { success, warn } = useToaster();
  const [availableClinics, setAvailableClinics] = useState<ISelectOption<Clinic>[]>([]);
  const [userCanSelectClinician, setUserCanSelectClinician] = useState(true);

  const userClinics = currentUser?.acl?.clinics || [];

  const [createPatient, { loading: isLoading }] = useMutation<
    GraphQLResponse<'createPatient', CreatePatientInput>
  >(PATIENT_CREATE);

  const [invitePatient] = useMutation<
    GraphQLResponse<'invitePatientViaDynamicLink', IInvitePatientArgs>
  >(PATIENT_INVITE);

  const { data: dataClinicians } = useQuery<{ listClinicians: ClinicianView[] }>(
    LIST_CLINICIANS_QUERY
  );

  // set up clinicians for autocomplete
  useEffect(() => {
    if (dataClinicians?.listClinicians)
      setClinicians(
        dataClinicians?.listClinicians.map(c => ({
          value: c.id.toString(),
          label: c.name,
          data: c,
        }))
      );
  }, [dataClinicians]);

  const { data: dataClinics } = useQuery<GraphQLResponseTitan<'clinics', Clinic[]>>(CLINICS_GET);

  // set up clinics for select
  useEffect(() => {
    if (dataClinics?.clinics) {
      // set available clinics for use in form
      setAvailableClinics(
        dataClinics.clinics
          .filter(c => userClinics.map(s => s.toString()).includes(c.id.toString()))
          .map(c => ({
            value: c.id.toString(),
            label: c.displayName,
            data: c,
          }))
      );

      const userClinic = dataClinics.clinics.find(
        c => c.id.toString() === currentUser?.clinicId.toString()
      );
      const clinicFeatureFlags = userClinic?.featureFlags || [];
      if (
        currentUser?.clinicianId &&
        !clinicFeatureFlags.includes('allow_clinician_create_non_self_patients')
      ) {
        setUserCanSelectClinician(false);
        setForm({
          ...form,
          clinicianId: currentUser.clinicianId,
        });
      }
    }
  }, [dataClinics, userClinics]);

  const handleSetInput = (key: InputFieldKey) => (e: ChangeEvent<HTMLInputElement>) => {
    setForm({
      ...form,
      [key]: e.target.value,
    });

    // clear out any errors on change to value
    setErrors({
      ...errors,
      [key]: '',
    });
  };

  const handleOnChangePhoneMobile = (countryCode: CountryCode, phoneNumber: string) => {
    setForm({
      ...form,
      phone: normalizePhoneNumber(countryCode, phoneNumber),
    });

    // clear out any errors on change to value
    setErrors({
      ...errors,
      phone: '',
    });
  };

  const handleSetSelect = (key: SelectFieldKey) => (e: ChangeEvent<HTMLSelectElement>) => {
    setForm({
      ...form,
      [key]: e.target.value as any,
    });

    // clear out any errors on change to value
    setErrors({
      ...errors,
      [key]: '',
    });
  };

  const handleSetDate = (key: DateFieldKey) => (value: Date | null) => {
    setForm({
      ...form,
      [key]: value,
    });

    // clear out any errors on change to value
    setErrors({
      ...errors,
      [key]: '',
    });
  };

  const handleSetCheckbox = (key: CheckboxFieldKey) => (e: ChangeEvent<HTMLInputElement>) => {
    setForm({
      ...form,
      [key]: e.target.checked,
    });
  };

  const handleSelectClinician = (option: ISelectOption<ClinicianView> | null) => {
    setForm({
      ...form,
      clinicianId: option ? (option.data as ClinicianView).id : null,
    });

    // clear out any errors on change to value
    setErrors({
      ...errors,
      clinicianId: '',
    });
  };

  const handleModalCancel = () => {
    setError('');
    setSignupCode('');
    setIsOpen(false);
  };

  const handleModalDone = () => {
    setForm(defaults);
    setIsOpen(false);
  };

  const handleReset = () => {
    setForm(defaults);
    setError('');
    setSignupCode('');
  };

  const handleSetSendInvite = (e: FormEvent<HTMLInputElement>) => {
    setSendInvite(e.currentTarget.checked);
  };

  const getErrorMessage = (key: keyof FormErrors): string => errors[key] || '';

  const hasErrors = (): boolean => {
    let errorsToSet: FormErrors = {};

    // check for required fields
    if (!form.nameFirst) errorsToSet = { ...errorsToSet, nameFirst: 'First name is required' };
    if (!form.nameLast) errorsToSet = { ...errorsToSet, nameLast: 'Last name is required' };
    if (!form.dob)
      errorsToSet = { ...errorsToSet, dob: 'Date formatted as DD-MM-YYYY is required' };
    if (!form.phone) {
      errorsToSet = { ...errorsToSet, phone: 'Phone number is required' };
    } else if (!phoneValidator(form.phone).isValid) {
      errorsToSet = { ...errorsToSet, phone: 'This number is not a valid mobile phone number' };
    }

    // check for checkboxes depending on desired notification method
    if (sendInvite && !form.allowSMS) {
      errorsToSet = { ...errorsToSet, sendInvite: 'Consent to send via SMS is required' };
    }

    // set errors
    setErrors(errorsToSet);

    // return if there are any errors
    return Object.keys(errorsToSet).length > 0;
  };

  const handleSubmit = async () => {
    if (hasErrors()) return;

    // clear out possible old values, i.e. on previous error
    setSignupCode('');
    setError('');

    // clean up values (numbers come in as strings from graphql, why?)
    const variables = {
      input: {
        nameFirst: form.nameFirst,
        nameLast: form.nameLast,
        email: form.email ? form.email : undefined,
        dob: format(form.dob as Date, 'yyyy-MM-dd'),
        phone: form.phone,
        clinicId: form.clinicId ? (Number(form.clinicId) as any) : null,
        clinicianId: form.clinicianId ? (Number(form.clinicianId) as any) : null,
        allowSms: form.allowSMS,
        allowEmail: form.allowEmail,
        riskLevel: form.riskLevel,
        progress: form.progress,
        sessionDate: form.sessionDate,
      },
    };

    try {
      // open up modal
      setIsOpen(true);

      const { data } = await createPatient({ variables });

      if (!data) {
        setError('No data returned from server');
        return;
      }

      const response = data?.createPatient as unknown as IPatient;
      if (data && response?.signupCode) {
        success('Patient registered successfully');

        // possibly send invite
        if (sendInvite) {
          try {
            await invitePatient({
              variables: {
                signupCode: response.signupCode,
              },
            });
            success('Invitation sent successfully');
          } catch (inviteErr: any) {
            warn('Patient registered but invitation failed. You can resend it later');
            logger.warn(new Error(`Failed to send invite: ${inviteErr.message}`));
          }
        }

        return setSignupCode(response.signupCode);
      }
    } catch (err: any) {
      const graphQLErrors = err.graphQLErrors || [];

      const authError = graphQLErrors.find(
        (e: any) => e.extensions?.code === 'AUTHORIZATION_ERROR'
      );

      if (authError) {
        setError(authError.message);
        logger.error(new Error(`Authorization error: ${authError.message}`));
      } else {
        setError(
          'There was an unknown error registering the patient. Please note the date and time and alert Tech Support.'
        );
        logger.error(new Error(`Error registering patient: ${err.message}`));
      }
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.tableWrapper}>
        <div className={styles.required}>
          Required fields marked with <span className={styles.asterisk}>*</span>
        </div>
        <div className={styles.table}>
          <div className={styles.column}>
            <Input.Field
              label="First Name"
              value={form.nameFirst}
              onChange={handleSetInput('nameFirst')}
              disabled={isLoading}
              required
              fullwidth
              errorMessage={getErrorMessage('nameFirst')}
            />
          </div>

          <div className={styles.column}>
            <Input.Field
              label="Last Name"
              value={form.nameLast}
              onChange={handleSetInput('nameLast')}
              disabled={isLoading}
              required
              fullwidth
              errorMessage={getErrorMessage('nameLast')}
            />
          </div>

          <div className={styles.column}>
            <DatePicker.Field
              label="Date of Birth"
              selected={form.dob}
              onChange={handleSetDate('dob')}
              disabled={isLoading}
              fullwidth
              required
              errorMessage={getErrorMessage('dob')}
            />
          </div>

          <div className={styles.column}>
            <Input.Field
              label="Email address"
              type="email"
              value={form.email}
              onChange={handleSetInput('email')}
              disabled={isLoading}
              fullwidth
              errorMessage={getErrorMessage('email')}
            />
          </div>

          <div className={styles.column}>
            <Input.Phone
              label="Phone Number"
              onChange={handleOnChangePhoneMobile}
              placeholder="07123456789"
              disabled={isLoading}
              required
              fullwidth
              errorMessage={getErrorMessage('phone')}
            />
          </div>

          <div className={styles.column}>
            <Select.Field
              label="Risk Level"
              value={form.riskLevel}
              options={riskLevels}
              onChange={handleSetSelect('riskLevel')}
              disabled={isLoading}
              required
              fullwidth
            />
          </div>

          <div className={styles.column}>
            <Checkbox
              label="Patient consents to sending email"
              checked={form.allowEmail}
              onChange={handleSetCheckbox('allowEmail')}
            />
            <Checkbox
              label="Patient consents to sending SMS"
              checked={form.allowSMS}
              onChange={handleSetCheckbox('allowSMS')}
            />
            <Checkbox
              label="Send SMS/Email invitation to patient now"
              checked={sendInvite}
              onChange={handleSetSendInvite}
              errorMessage={getErrorMessage('sendInvite')}
            />
          </div>

          <div className={styles.column}>
            <Autocomplete.Field
              label="Assigned Clinician"
              placeholder="Search for name or email"
              options={clinicians}
              onSelect={handleSelectClinician}
              filterOn={onFilterClinician}
              disabled={!userCanSelectClinician || isLoading}
              RenderOption={ClinicianOption}
              style={{ maxWidth: '500px' }}
            />
          </div>

          <div className={styles.column}>
            <DatePicker.Field
              label="First Session Date"
              selected={form.sessionDate}
              onChange={handleSetDate('sessionDate')}
              disabled={isLoading}
              fullwidth
            />
          </div>
        </div>

        <div className={styles.table}>
          <div className={styles.column}>
            <Select.Field
              label="Clinic"
              value={form.clinicId}
              options={availableClinics}
              onChange={handleSetSelect('clinicId')}
              disabled={isLoading}
              fullwidth
              usePlaceholderChild
              required
            />
          </div>
        </div>

        <div className={styles.table}>
          <div className={clsx('buttons', styles.buttons)}>
            <Button
              onClick={handleReset}
              disabled={isLoading}
              style={{
                height: '45px',
                width: '100px',
                fontSize: '14px',
                justifyContent: 'center',
              }}>
              Reset
            </Button>

            <Button
              variant="dark"
              onClick={handleSubmit}
              disabled={isLoading}
              dot="green"
              style={{
                height: '45px',
                width: '160px',
                fontSize: '14px',
              }}>
              Register
            </Button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isOpen}
        onClose={handleModalCancel}
        title="Register new patient"
        contentProps={{
          style: {
            padding: '1.5rem',
            flexDirection: 'column',
          },
        }}
        style={{
          content: {
            width: '80%',
            maxWidth: '950px',
          },
        }}>
        <div>
          {isLoading && (
            <div>
              <h2 className="is-size-5">Connecting to server</h2>
              <Loader loading={isLoading} />
            </div>
          )}

          {Boolean(signupCode) && (
            <div>
              <h2 className="is-size-5">
                Got it! Here&apos;s the Limbic Signup Code:
                <span className={styles.signupCode}>{signupCode}</span>
              </h2>
            </div>
          )}

          {Boolean(error) && (
            <div>
              <h2 className="is-size-5">The following error was returned:</h2>
              <div className={styles.error}>
                <p>{error}</p>
              </div>
            </div>
          )}
        </div>

        <div className={clsx('buttons', styles.buttons)}>
          {Boolean(error) && (
            <Button onClick={handleModalCancel} disabled={isLoading}>
              Go Back
            </Button>
          )}

          {Boolean(signupCode) && (
            <Button
              onClick={handleModalDone}
              variant="dark"
              isLoading={isLoading}
              disabled={isLoading}>
              Done
            </Button>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default PatientsRegisterView;
